import { Injectable } from "@angular/core";
import { NotificationsService } from "angular2-notifications";
import { BehaviorSubject, Observable, map } from "rxjs";
import { apiCallWrapper, httpParams, queryToParams } from "../api/api.util";
import { IQueryFilter, QueryResult } from "../model/query.filter.class";
import { ProductApi } from "../api/product.api";
import { HasId } from "../model/generics";
import {
  Category,
  IProductDrafts,
  IRequestPrdoductPayload,
  IValidateProductUriOpts,
  NewProduct,
} from "../model/ddb.model";
import { SessionApi } from "../api/session.api";
import { LoadingService } from "./core/loading.service";
import { duration } from "moment-mini";

@Injectable()
export class ProductService {
  private sortSubject = new BehaviorSubject<string | undefined>(undefined);
  private supplierSortSubject = new BehaviorSubject<number | undefined>(
    undefined,
  );
  private sortFilterSubject = new BehaviorSubject<boolean>(true);
  private gender$ = new BehaviorSubject<string | null>(null);

  constructor(
    private notifications: NotificationsService,
    private productApi: ProductApi,
    private session: SessionApi,
    private loadingService: LoadingService,
  ) {}

  public list(
    query: IQueryFilter,
    additionalParams: { [key: string]: any } = {},
    showNotification = true,
  ): Observable<QueryResult<HasId & NewProduct>> {
    let params = queryToParams(query);

    for (let prop in additionalParams) {
      params = params.append(prop, additionalParams[prop]);
    }
    return apiCallWrapper(
      this.productApi.productList(params),
      showNotification
        ? {
            notificationsService: this.notifications,
            action: "Fetching products",
          }
        : undefined,
    );
  }

  readonly getProductList = (
    query: IQueryFilter,
    additionalParams: { [key: string]: any } = {},
  ) => {
    let params = queryToParams(query);

    for (let prop in additionalParams) {
      params = params.append(prop, additionalParams[prop]);
    }

    return this.loadingService
      .blockWithLoadingOverlayRx(this.productApi.getProductList(params))
      .pipe(
        map((result) => {
          result.rows = result.rows.map((item) => {
            let instance = new NewProduct();
            Object.assign(instance, item);
            return instance;
          });
          return result;
        }),
      );
  };

  readonly getUnAuthProductList = (
    query: IQueryFilter,
    additionalParams: { [key: string]: any } = {},
  ) => {
    let params = queryToParams(query);
    for (let prop in additionalParams) {
      params = params.append(prop, additionalParams[prop]);
    }
    return this.productApi.getUnAuthProductList(params).pipe(
      map((result) => {
        result.rows = result.rows.map((item) => {
          let instance = new NewProduct();
          Object.assign(instance, item);
          return instance;
        });
        return result;
      }),
    );
  };

  readonly getAllocationProductList = (query: IQueryFilter) => {
    let params = queryToParams(query);
    return this.loadingService
      .blockWithLoadingOverlayRx(
        this.productApi.getAllocationProductList(params),
      )
      .pipe(
        map((result) => {
          if (result.length == 0) {
            return { rows: [], count: 0 };
          }
          result.rows = result.rows.map((item) => {
            let instance = new NewProduct();
            Object.assign(instance, item);
            return instance;
          });
          return result;
        }),
      );
  };

  readonly getProductById = (
    id: number | string,
    includeArchived: boolean = false,
  ): Observable<NewProduct> => {
    const params: any = {};
    if (includeArchived) {
      params.scope = "IncludeArchived";
    }
    return this.productApi.get(id).pipe(
      map((result) => {
        let instance = new NewProduct();

        Object.assign(instance, result);

        if (instance.variations && instance.variations.length) {
          instance.variations = instance.variations.sort((a, b) => {
            if (a.displayOrder !== null && b.displayOrder !== null) {
              if (a.displayOrder === b.displayOrder && a.id && b.id) {
                return a.id - b.id;
              } else {
                return a.displayOrder - b.displayOrder;
              }
            } else {
              if (a.displayOrder === null && b.displayOrder === null) {
                return 0;
              } else if (a.displayOrder === null) {
                return 1;
              } else {
                return -1;
              }
            }
          });
        }

        /** Order the categories in accordance with their parent */
        if (instance.categories.length) {
          const sortedCategories: any[] = [];
          while (instance.categories.length) {
            if (!sortedCategories.length) {
              let topmostCategory;
              for (let n = 0; n < instance.categories.length; n++) {
                if (instance.categories[n].parentId === null) {
                  topmostCategory = instance.categories[n];
                  instance.categories.splice(n, 1);
                  break;
                }
              }

              if (!topmostCategory) break;

              sortedCategories.push(topmostCategory);
            } else {
              let lastCategory = sortedCategories[sortedCategories.length - 1];
              let nextCategory: Category | null = null;
              for (let n = 0; n < instance.categories.length; n++) {
                if (instance.categories[n].parentId === lastCategory.id) {
                  nextCategory = instance.categories[n];
                  instance.categories.splice(n, 1);
                  break;
                }
              }

              if (!nextCategory) break;

              sortedCategories.push(nextCategory);
            }
          }

          instance.categories = sortedCategories;
        }

        return instance;
      }),
    );
  };

  public getProductDraft(id?: number): Observable<IProductDrafts> {
    return apiCallWrapper(this.productApi.getProductDraft(id), {
      notificationsService: this.notifications,
      action: "Fetching Draft",
    });
  }

  readonly validateProductUri = (
    data: IValidateProductUriOpts,
  ): Observable<{ unique: boolean }> => {
    return apiCallWrapper(this.productApi.validateProductUri(data), {
      notificationsService: this.notifications,
      action: "Validate Product URI",
    });
  };

  public create(data: NewProduct) {
    return apiCallWrapper(
      this.loadingService.blockWithLoadingOverlayRx(
        this.productApi.create(data),
      ),
      data.id
        ? {
            notificationsService: this.notifications,
            action: "Update Product",
          }
        : {
            notificationsService: this.notifications,
            action: "Create Product",
          },
    );
  }

  public cloneProduct(product: NewProduct) {
    return apiCallWrapper(
      this.loadingService.blockWithLoadingOverlayRx(
        this.productApi.cloneProduct(product),
      ),
      {
        notificationsService: this.notifications,
        action: "Clone Product",
      },
    );
  }

  public deleteProduct(id: number | string) {
    return apiCallWrapper(
      this.loadingService.blockWithLoadingOverlayRx(
        this.productApi.deleteProduct(id),
      ),
      {
        notificationsService: this.notifications,
        action: "Delete Product",
      },
    );
  }

  public restoreProduct(id: number | string) {
    return apiCallWrapper(
      this.loadingService.blockWithLoadingOverlayRx(
        this.productApi.restoreProduct(id),
      ),
      {
        notificationsService: this.notifications,
        action: "Restore Product",
      },
    );
  }

  public readonly getProductByUri = (uri: string) => {
    let params = httpParams();

    return this.productApi.getProductByUri(params, uri).pipe(
      map((result) => {
        let instance = new NewProduct();

        Object.assign(instance, result);

        return instance;
      }),
    );
  };

  public readonly getUnAuthProductByUri = (uri: string) => {
    let params = httpParams();
    return this.productApi.getUnAuthProductByUri(params, uri).pipe(
      map((result) => {
        let instance = new NewProduct();

        Object.assign(instance, result);

        return instance;
      }),
    );
  };

  setSort(sort: string | undefined): void {
    this.sortSubject.next(sort);
  }

  getSort(): Observable<string | undefined> {
    return this.sortSubject.asObservable();
  }

  getTopProducts(query: IQueryFilter) {
    let params = queryToParams(query);
    return this.productApi.getTopProducts(params);
  }

  setFilterSort(sort: boolean): void {
    this.sortFilterSubject.next(sort);
  }

  getFilterSort(): Observable<boolean> {
    return this.sortFilterSubject.asObservable();
  }

  setSupplier(sort: number | undefined): void {
    this.supplierSortSubject.next(sort);
  }

  getSupplier(): Observable<number | undefined> {
    return this.supplierSortSubject.asObservable();
  }

  public readonly requestProduct = (payload: IRequestPrdoductPayload) => {
    return this.loadingService
      .blockWithLoadingOverlayRx(this.productApi.requestProduct(payload))
      .pipe(
        map((result) => {
          let instance = new NewProduct();

          Object.assign(instance, result);

          return instance;
        }),
      );
  };

  setGender(gender: string | null): void {
    this.gender$.next(gender);
  }

  getGender() {
    return this.gender$.asObservable();
  }
}
